/* eslint-disable max-classes-per-file */
import axiosStatic, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { Approval } from 'api/approval';
import { axios } from 'api/axios';
import { Task, Task as TaskApi } from 'api/tasks';
// import { HttpClient } from "../http-client";
import type { Invoice } from 'components/organisms/invoices/types';
import {
  getCurrentApiHost,
  API_NAMESPACE,
  API_VERSION,
} from 'config/environment';
import { Project } from '../project';
import { Space, SpaceTab } from '../space';

abstract class HttpClient {
  private _api: AxiosInstance;

  protected api = () => this._api;

  /**
   * this base url is used to generate download url
   * for project files.
   * these download urls will be set as href for anchor tags.
   */
  // eslint-disable-next-line class-methods-use-this -- getter returns dynamic host, no instance state
  protected get BASEURL() {
    return `${getCurrentApiHost()}/${API_NAMESPACE}/${API_VERSION}`;
  }

  public constructor() {
    this._api = axiosStatic.create({
      baseURL: this.BASEURL,
    });
    axios().subscribe((client) => {
      this._api = client;
    });
  }

  public get = <T, R = T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<R> => {
    return this.api()
      .get(url, config)
      .then((x) => x.data);
  };

  public getResponse = <T, R = T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<R>> => {
    return this.api()
      .get(url, config)
      .then((x) => x);
  };

  public post = <T, B, R = T>(
    url: string,
    payload?: B,
    config?: AxiosRequestConfig,
  ): Promise<R> =>
    this.api()
      .post(url, payload, config)
      .then((x) => x.data);

  public put = <T, B, R = T>(
    url: string,
    payload?: B,
    config?: AxiosRequestConfig,
  ): Promise<R> =>
    this.api()
      .put(url, payload, config)
      .then((x) => x.data);

  public patch = <T, B, R = T>(
    url: string,
    payload?: B,
    config?: AxiosRequestConfig,
  ): Promise<R> =>
    this.api()
      .post(url, payload, config)
      .then((x) => x.data);

  public delete = <T, R = T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<R> =>
    this.api()
      .delete(url, config)
      .then((x) => x.data);
}

export { HttpClient };

export enum ApprovalType {
  TaskApproval = 'TASK',
  SpaceTabApproval = 'SPACE_TAB',
  InvoiceApproval = 'INVOICE',
}

export type HomeSpaceTab = Pick<SpaceTab, 'spaceTabId' | 'spaceTabTitle'> & {
  space: Pick<Space, 'spaceId' | 'spaceName'> & {
    project: Pick<Project, 'projectId' | 'projectName'>;
  };
};

export interface TaskApproval extends Approval {
  associatedEntity: TaskApi;
  associatedEntityType: ApprovalType.TaskApproval;
}

interface SpaceTabApproval extends Approval {
  associatedEntity: HomeSpaceTab;
  associatedEntityType: ApprovalType.SpaceTabApproval;
}

export interface InvoiceApproval extends Approval {
  associatedEntity: Pick<
    Invoice,
    'invoiceId' | 'invoiceNumber' | 'invoiceNumberPrefix'
  >;
  associatedEntityType: ApprovalType.InvoiceApproval;
}

export type ApprovalWithAssociatedEntity =
  | TaskApproval
  | SpaceTabApproval
  | InvoiceApproval;

export interface HomeResponse {
  myTasks: Array<TaskApi>;
  approvals: Array<ApprovalWithAssociatedEntity>;
}

export interface CsatAndApprovalCount {
  [key: number]: { approvalCount: number; csatCount: number };
}

class HomeHttpClient extends HttpClient {
  public async getCsatAndApprovalsCount(
    companyIds?: string | undefined,
  ): Promise<CsatAndApprovalCount> {
    return this.get<CsatAndApprovalCount>(
      `/home/action-items/aggregated-counts?companyIds=${companyIds}`,
    );
  }

  public async getHomeScreenResponse(): Promise<HomeResponse> {
    return this.get<HomeResponse>('/home');
  }

  public async getCsat(companyId: number | undefined): Promise<Task[]> {
    return this.get<Task[]>(
      `/home/get-csat${companyId ? `?companyId=${companyId}` : ''}`,
    );
  }

  public async getMyCompanyTasks(): Promise<Task[]> {
    return this.get<Task[]>('/home/my-company-tasks');
  }

  public async getAllApprovals(): Promise<ApprovalWithAssociatedEntity[]> {
    return this.get<ApprovalWithAssociatedEntity[]>('/home/pending-approvals');
  }
}

export const HomeAPI = new HomeHttpClient();
